import React, { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { AxiosRequestConfig } from "axios";
import { TableCell } from "@mui/material";
import TableTemplate, { IAdditional, ITableUI } from "../../Tables/TableTemplate/tableTemplate";
import { CommonTableUI } from "../../Tables/TableTemplate/common";
import Cell from "../../Tables/TableTemplate/Cell";
import { ITableReport, Report } from "../../Interfaces/TableInterfaces";
import { KaminPage } from "../../Requests/kamin2leg";

export interface KaminColumn {
  title: string;
  dataIndex?: string;
  render?: (value: any, row: any) => React.ReactNode;
}
interface KaminActionColumn {
  key: string;
  header?: React.ReactNode;
  render: (row: any) => React.ReactNode;
}
interface Props {
  columns: KaminColumn[];
  actionColumns?: KaminActionColumn[];
  rows?: any[];
  getPage?: (offset: number, pageSize: number, signal: AbortSignal) => Promise<KaminPage<any>>;
  refreshKey?: number;
  selected?: React.Key[];
  onSelect: (ids: React.Key[]) => void;
  onRowClick?: (row: any) => void;
  actionsHeader?: React.ReactNode;
}
interface ReportRow extends ITableReport { data: any; }
const TableContext = createContext<Props | undefined>(undefined);
const additional: IAdditional<ReportRow> = {
  getHeader: function ActionsHeader() {
    const props = useContext(TableContext)!;
    if (props.actionColumns) return <>{props.actionColumns.map(column =>
      <TableCell key={column.key}>{column.header}</TableCell>)}</>;
    return <TableCell className="!sticky !left-0 !z-50 fixed-column">{props.actionsHeader || "عملیات"}</TableCell>;
  },
  getRow: function ActionsRow({ row }) {
    const props = useContext(TableContext)!;
    if (props.actionColumns) return <>{props.actionColumns.map(column =>
      <TableCell key={column.key} onClick={e => e.stopPropagation()}>{column.render(row.data)}</TableCell>)}</>;
    return <TableCell className="!sticky !left-0 !z-50 fixed-column" onClick={e => e.stopPropagation()}>
      {props.columns.find(c => c.title === "عملیات")?.render?.(undefined, row.data)}
    </TableCell>;
  },
};

const TableUI: React.FC<ITableUI<ReportRow>> = ({ reports, onAction, scrollKey }) => {
  const props = useContext(TableContext)!;
  const columns = props.columns.filter(c => c.title !== "عملیات");
  const headers = columns.map((column, i) => ({ headerName: `Column${i}`, translateHeaderName: column.title,
    typeStr: "Normal", positionTypeStr: i === 0 ? "FixedFirst" : "FirstPage" }));
  return <CommonTableUI<ReportRow> key={`${reports.offset}-${reports.pageSize}-${props.refreshKey || 0}`}
    reports={{ ...reports, translatedHeaders: headers }} onAction={onAction} scrollKey={scrollKey}
    withSelect={props.selected !== undefined} stickySelectColumn={props.selected !== undefined}
    isRowSelectable={row => !row.data.deletedAt} onSelectedRowsChange={props.onSelect}
    onTableRowClick={props.onRowClick ? row => props.onRowClick!(row.data) : undefined}
    isRowClickable={() => !!props.onRowClick}
    AdditionalBody={props.actionColumns?.length || props.columns.some(c => c.title === "عملیات") ? additional : undefined}
    tableBoxClassName="max-h-[500px] overflow-auto"
    getExtraTypeUI={({ row, headerName }) => {
      const column = columns[Number(headerName.replace("Column", ""))];
      const value = column.dataIndex ? row.data[column.dataIndex] : undefined;
      return <>{column.render ? column.render(value, row.data)
        : <Cell clonedRow={{ value }} headerName="Value" type="Normal" />}</>;
    }} />;
};

export default function KaminTable(props: Props) {
  const lastPage = useRef("");
  const searchParameters = useMemo(() => ({}), [props.getPage, props.rows]);
  const getDataTable = useCallback(async (_: {}, offset: number | null, pageSize: number | null,
    _sorting?: string, _additional?: unknown, options?: AxiosRequestConfig): Promise<Report<ReportRow>> => {
    const start = offset || 0; const size = pageSize || 25;
    const page: Partial<KaminPage<any>> & { items: any[]; count: number } = props.getPage
      ? await props.getPage(start, size, options?.signal as AbortSignal || new AbortController().signal)
      : { items: (props.rows || []).slice(start, start + size), count: props.rows?.length || 0 };
    const pageKey = `${start}-${size}`;
    if (lastPage.current && lastPage.current !== pageKey) props.onSelect([]);
    lastPage.current = pageKey;
    return { report: page.report || page.items.map((data, i) => ({
      id: typeof data.id === "number" ? data.id : start + i + 1, row: start + i + 1, data })),
      count: page.count, offset: start, pageSize: size,
      translatedHeaders: page.translatedHeaders || [{ headerName: "Column0", translateHeaderName: "#" }] };
  }, [props.getPage, props.rows]);
  return <TableContext.Provider value={props}>
    <div className="overflow-auto order-table w-full">
      <TableTemplate<{}, ReportRow> getDataTable={getDataTable} TableUI={TableUI}
        tableWrapperClassname="w-full min-w-0"
        searchParameters={searchParameters} refreshTableTime={props.getPage ? 5000 : undefined}
        silentRefreshKey={props.refreshKey} pagesize={25} showPageSize showIsLoading={false} />
    </div>
  </TableContext.Provider>;
}
